/*
 * Size guide panel (settings.size_guide_enabled).
 *
 * Opening, closing, the per-row photo strip, and one piece of wiring back to the picker:
 * a row's button chooses that size, the panel closes, and the shopper is scrolled to the
 * next step rather than back where they were.
 *
 * Choosing does NOT touch the hidden Shopify.OptionSelectors <select>. It forwards the
 * click to the picker's own anchor, because product-page.js binds its handler to that
 * anchor, and doing the work twice here is how the two get out of step.
 *
 * Everything the panel shows is server-rendered, so with this file absent or slow the
 * guide is a panel that will not open and a strip that still swipes — no half-built state.
 */
(function () {
  'use strict';

  var ROOT = '.js-size-guide';

  function panel() {
    return document.querySelector(ROOT);
  }

  /*
   * The page behind the panel must not scroll. Lenis owns the scroll when smooth scrolling
   * is on and setting overflow under it does nothing, which is why this is the same
   * two-branch dance the cart and contact drawers do rather than one line.
   */
  function freeze() {
    if (window.lenis) {
      window.lenis.stop();
    } else {
      document.body.style.overflow = 'hidden';
    }
  }

  function thaw() {
    if (window.lenis) {
      window.lenis.start();
    } else {
      document.body.style.overflow = '';
    }
  }

  var lastTrigger = null;

  function open(trigger) {
    var el = panel();
    if (!el || el.classList.contains('active')) return;

    lastTrigger = trigger || null;
    el.classList.add('display');
    freeze();

    // Two frames, not one: the transform has to be painted in its closed position before
    // it is released, or the panel appears open with no transition.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('active');
        var box = el.querySelector('.size-guide__panel');
        if (box) box.focus();
      });
    });
  }

  function close(done) {
    var el = panel();
    if (!el || !el.classList.contains('active')) return;

    el.classList.add('closing');

    setTimeout(function () {
      el.classList.remove('active', 'closing', 'display');
      thaw();
      // Anything that scrolls has to run AFTER thaw(): while the drawer is open lenis is
      // stopped, and a scroll requested against a stopped lenis goes nowhere.
      if (done) {
        done();
      } else if (lastTrigger && document.contains(lastTrigger)) {
        lastTrigger.focus();
      }
      lastTrigger = null;
    }, 300);
  }

  /*
   * Delegated from the document rather than bound to the nodes. The panel and its triggers
   * are server-rendered inside a section, so the theme editor can replace either of them
   * under us; a delegated listener survives that, a bound one does not.
   */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('.js-size-guide-open');
    if (opener) {
      event.preventDefault();
      open(opener);
      return;
    }

    if (event.target.closest('.js-size-guide-close')) {
      event.preventDefault();
      close();
      return;
    }

    var choose = event.target.closest('.js-size-guide-pick');
    if (choose) {
      event.preventDefault();
      var anchor = pick(choose.getAttribute('data-value'));
      close(function () {
        advance(anchor);
      });
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    close();
  });

  /*
   * Forwarded to the picker's anchor, which is the only thing bound to the variant
   * machinery. Scoped by the option number the panel was rendered with, so a product whose
   * size is option2 still hits the size selector and not the frame one, and the classic
   * and stepped pickers are both reached by the same lookup because their anchors carry
   * the same class and data attributes.
   */
  function pick(value) {
    var el = panel();
    if (!el || !value) return;

    var option = el.getAttribute('data-option');
    if (!option) return;

    var anchors = document.querySelectorAll('.js-product-size[data-option="' + option + '"]');
    for (var i = 0; i < anchors.length; i++) {
      if (anchors[i].getAttribute('data-value') === value) {
        anchors[i].click();
        return anchors[i];
      }
    }
  }

  /*
   * Choosing a size answers the question the panel was opened with, so the shopper is put
   * at the NEXT question rather than back where they were — on this product that is the
   * frame, which is the step add-to-cart-from-the-panel would have skipped.
   *
   * Walks forward from the size step in whichever picker is rendered; falls back to the add
   * to cart bar when the size is the last step, and does nothing at all if neither is found.
   * scrollIntoView is what the rest of this section uses, so it behaves the same under lenis.
   */
  function advance(anchor) {
    if (!anchor) return;

    var target = null;
    var step = anchor.closest('.product-v2__step');

    if (step) {
      var next = step.nextElementSibling;
      if (next && next.classList.contains('product-v2__step')) target = next;
    }

    if (!target) target = document.querySelector('.js-wrap-add-to-cart:not(.d-none)');
    if (!target) target = anchor.closest('.product__options') || step;
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /*
   * Dots follow the strip's own scroll position rather than a slide index this file keeps.
   * The strip is scrolled by the browser — swipe, trackpad, shift-wheel, a snap settling
   * after a flick — and only the scroll position knows about all of those.
   *
   * rAF-throttled because scroll fires far faster than paint, and every handler here is a
   * read of scrollLeft, which forces layout if it lands mid-frame.
   */
  function bindDots(strip) {
    var slides = strip.querySelector('.js-size-guide-slides');
    var dots = strip.querySelector('.js-size-guide-dots');
    if (!slides || !dots) return;

    var marks = dots.children;
    var queued = false;

    slides.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        var width = slides.clientWidth;
        if (!width) return;
        var index = Math.round(slides.scrollLeft / width);
        for (var i = 0; i < marks.length; i++) {
          marks[i].classList.toggle('is-active', i === index);
        }
      });
    }, { passive: true });
  }

  document.querySelectorAll('.size-guide__strip').forEach(bindDots);

})();
